export type AuthFlow = 'admin' | 'pilot';

export const ADMIN_AUTH_PENDING_KEY = 'tst-admin-auth-pending';
export const PILOT_AUTH_PENDING_KEY = 'tst-pilot-auth-pending';
export const PILOT_AUTH_STORAGE_KEY = 'tst-pilot-auth-token';
export const ADMIN_AUTH_STORAGE_KEY = 'tst-admin-auth-token';
export const AUTH_PENDING_TTL_MS = 10 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function isSupabaseAuthCallback(search: string, hash: string): boolean {
  return hasAuthParam(search) || hasAuthParam(hash);
}

export function authCallbackSuffix(search: string, hash: string): string {
  return `${search}${hash}`;
}

export function markAuthPending(
  flow: AuthFlow,
  storage: StorageLike = window.localStorage,
  now = Date.now(),
): boolean {
  try {
    storage.setItem(keyFor(flow), String(now));
    return true;
  } catch {
    return false;
  }
}

export function clearAuthPending(
  flow: AuthFlow,
  storage: StorageLike = window.localStorage,
): void {
  try {
    storage.removeItem(keyFor(flow));
  } catch {
    // Storage is optional for auth routing. The callback should fall back to pilot.
  }
}

export function consumeAuthCallbackFlow(
  storage: StorageLike = window.localStorage,
  now = Date.now(),
): AuthFlow {
  const admin = readPending('admin', storage, now);
  const pilot = readPending('pilot', storage, now);
  clearAuthPending('admin', storage);
  clearAuthPending('pilot', storage);

  if (admin !== null && (pilot === null || admin > pilot)) return 'admin';
  return 'pilot';
}

function keyFor(flow: AuthFlow): string {
  return flow === 'admin' ? ADMIN_AUTH_PENDING_KEY : PILOT_AUTH_PENDING_KEY;
}

function readPending(flow: AuthFlow, storage: StorageLike, now: number): number | null {
  try {
    const raw = storage.getItem(keyFor(flow));
    if (!raw) return null;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return null;
    return now - ts < AUTH_PENDING_TTL_MS ? ts : null;
  } catch {
    return null;
  }
}

function hasAuthParam(part: string): boolean {
  if (!part) return false;
  return (
    part.includes('access_token=') ||
    part.includes('error=') ||
    part.includes('type=magiclink') ||
    part.includes('code=')
  );
}
